import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BookUser,
  CalendarDays,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  FileText,
  Info,
  MapPin,
  Share2,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL, ENTITY_TYPE_SELLER_OUTLET } from "utils/constants/others";
import { DownloadInvoice } from "operations/operations";
import { convertInvoiceImagesToPdf, isImageUrl } from "utils/pdfUtils";
import { isSupportedInvoiceImage } from "./planShipmentInvoiceUtils";
import {
  ASN_DELIVERY_TYPES,
  ASN_TRANSPORTERS,
  buildCreateAdvanceShippingNoteRequest,
  buildUpdateAdvanceShippingNoteRequest,
  getCreatedShippingNoteDocument,
  getSelectedShippingNoteInvoices,
  getShippingNoteSummary,
  MAX_ASN_DOCUMENT_SIZE_BYTES,
  validateShippingNoteForm,
} from "./planShipmentASNUtils";
import { getShippingNoteDocument } from "./planShipmentShippingNotesUtils";
import {
  formatInvoiceAmount,
  formatPurchaseOrderCount,
  formatScheduleDate,
  formatScheduleGroupNumber,
  formatShippingNoteNumber,
  formatSlotDisplayName,
  formatStatus,
  formatWeight,
} from "./planShipmentUtils";
import usePlanShipmentStore from "./usePlanShipmentStore";
import {
  BottomActions,
  CreateShippingNoteButton,
  DetailHeader,
  DetailHeaderCopy,
  DetailPageFrame,
  DetailPageShell,
  DetailScrollArea,
  DetailStatus,
  SummaryIcon,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
} from "./styles.scheduleGroupDetails";
import MapLinkButton from "components/shared/MapLinkButton";
import {
  EmptyState,
  ErrorMessage,
  HeaderIconButton,
  RetryButton,
  SkeletonCard,
  SkeletonLine,
  StateTitle,
} from "./styles.planShipment";
import {
  ConfirmationActions,
  ConfirmationBody,
  ConfirmationIcon,
  ConfirmationModal,
  ConfirmationSummary,
  ConfirmationTitle,
  CustomDropdownButton,
  CustomDropdownLabel,
  CustomDropdownMenu,
  CustomDropdownOption,
  CustomDropdownWrapper,
  DeliveryRadio,
  DeliveryTypeButton,
  DeliveryTypeGrid,
  DocumentHelp,
  DocumentPicker,
  DocumentPickerRow,
  DocumentPreview,
  DocumentPreviewLink,
  DocumentSection,
  DocumentTitle,
  FieldError,
  FieldInput,
  FieldLabel,
  FieldStack,
  FieldWrapper,
  HelpImageContainer,
  HiddenFileInput,
  InputInnerWrapper,
  InputSuffixButton,
  ModalBackdrop,
  ModalButton,
  PhoneDisclaimer,
  RemoveDocumentButton,
  SelectedInvoiceSummary,
  ShipmentDetailsHeading,
  ShipmentFormCard,
  ShipmentSummaryCard,
  ShipmentSummaryGrid,
  ShippingNoteContent,
  Spinner,
  SubmissionError,
  ShareButton,
  SuccessActions,
  SuccessActionButton,
  SuccessBody,
  SuccessIcon,
  SuccessPage,
  SuccessTitle,
} from "./styles.shippingNoteDetails";
import { resolveDocumentUrl } from "utils/documentUtils";

const PAGE_SIZE = 20;

const DOCUMENT_KEY_EWAY_BILL = "ewayBillFiles";
const DOCUMENT_KEY_COURIER_RECEIPT = "courierReceiptFiles";


const TransporterSelect = ({ value, onChange, onBlur, options, error, label, touched = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }

    return undefined;
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <CustomDropdownWrapper ref={dropdownRef}>
      {value && <CustomDropdownLabel $error={error}>{label}</CustomDropdownLabel>}
      <CustomDropdownButton
        type="button"
        $open={isOpen}
        $error={error && touched}
        $selected={!!value}
        onClick={() => setIsOpen(!isOpen)}
        onBlur={onBlur}
      >
        <span>{value || label}</span>
        <ChevronDown size={20} />
      </CustomDropdownButton>
      {isOpen && (
        <CustomDropdownMenu>
          {options.map((option) => (
            <CustomDropdownOption
              key={option}
              type="button"
              $selected={value === option}
              onClick={() => handleSelect(option)}
            >
              {option}
            </CustomDropdownOption>
          ))}
        </CustomDropdownMenu>
      )}
      {error && touched && <FieldError>{error}</FieldError>}
    </CustomDropdownWrapper>
  );
};

const ShippingNoteSkeleton = () => (
  <ShippingNoteContent aria-label="Loading shipment details">
    <SkeletonCard>
      <SkeletonLine $height="150px" />
    </SkeletonCard>
    <SkeletonLine $width="48%" $height="26px" />
    <SkeletonCard>
      <SkeletonLine $height="80px" />
      <SkeletonLine $height="80px" />
      <SkeletonLine $height="80px" />
    </SkeletonCard>
  </ShippingNoteContent>
);

const FloatingInput = ({
  label,
  value,
  error,
  onBlur,
  onChange,
  suffix,
  ...inputProps
}) => (
  <FieldWrapper>
    {value ? <FieldLabel $error={Boolean(error)}>{label}</FieldLabel> : null}
    <InputInnerWrapper>
      <FieldInput
        {...inputProps}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
        placeholder={label}
        $error={Boolean(error)}
        $hasSuffix={Boolean(suffix)}
      />
      {suffix || null}
    </InputInnerWrapper>
    {error ? <FieldError>{error}</FieldError> : null}
  </FieldWrapper>
);

const DocumentUploader = ({
  id,
  title,
  help,
  files,
  error,
  onSelect,
  onRemove,
  existingDoc,
  onRemoveExisting,
}) => {
  const imageInputId = `${id}-image`;
  const pdfInputId = `${id}-pdf`;
  const hasFiles = files.length > 0;
  const showExisting = existingDoc && !hasFiles;
  const hasImageFiles = hasFiles && files.some(
    (f) => !(f.file?.type === "application/pdf" || /\.pdf$/i.test(f.file?.name || ""))
  );
  const hasPdfFile = hasFiles && files.some(
    (f) => f.file?.type === "application/pdf" || /\.pdf$/i.test(f.file?.name || "")
  );
  // Show camera if no existing doc and no PDF uploaded yet
  const showImagePicker = !showExisting && !hasPdfFile;
  // Show PDF picker only when no files uploaded at all
  const showPdfPicker = !showExisting && !hasFiles;

  return (
    <DocumentSection>
      <DocumentTitle>{title}</DocumentTitle>
      <DocumentHelp>{help}</DocumentHelp>
      <DocumentPickerRow>
        {showExisting ? (
          <ExistingDocumentPreview doc={existingDoc} onRemove={onRemoveExisting} />
        ) : null}

        {showImagePicker ? (
          <DocumentPicker htmlFor={imageInputId} aria-label={`${title} – upload image`}>
            <Camera aria-hidden="true" />
            <HiddenFileInput
              id={imageInputId}
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              multiple
              onChange={onSelect}
            />
          </DocumentPicker>
        ) : null}

        {showPdfPicker ? (
          <DocumentPicker htmlFor={pdfInputId} aria-label={`${title} – upload PDF`}>
            <FileText aria-hidden="true" />
            <HiddenFileInput
              id={pdfInputId}
              type="file"
              accept="application/pdf,.pdf"
              onChange={onSelect}
            />
          </DocumentPicker>
        ) : null}

        {files.map((fileItem, index) => {
          const isPdf =
            fileItem.file?.type === "application/pdf" ||
            /\.pdf$/i.test(fileItem.file?.name || "");
          return (
            <DocumentPreview key={fileItem.client_id}>
              {isPdf ? (
                <DocumentPreviewLink
                  href={fileItem.preview_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="View PDF"
                >
                  <FileText size={26} aria-hidden="true" />
                  <span style={{ fontSize: "10px", lineHeight: 1 }}>PDF</span>
                </DocumentPreviewLink>
              ) : (
                <img src={fileItem.preview_url} alt="" />
              )}
              <RemoveDocumentButton
                type="button"
                onClick={() => onRemove(index)}
                aria-label={`Remove ${fileItem.file.name}`}
              >
                <X aria-hidden="true" />
              </RemoveDocumentButton>
            </DocumentPreview>
          );
        })}
      </DocumentPickerRow>
      {error ? <FieldError>{error}</FieldError> : null}
    </DocumentSection>
  );
};

const SummaryRow = ({ label, value }) => (
  <>
    <dt>{label}</dt>
    <dd>{value}</dd>
  </>
);

const ExistingDocumentPreview = ({ doc, onRemove }) => {
  if (!doc?.path) return null;
  const resolvedUrl = resolveDocumentUrl(doc.path);
  const isImage = isImageUrl(doc.path);

  return (
    <DocumentPreview>
      {isImage ? (
        <img src={resolvedUrl} alt="" />
      ) : (
        <DocumentPreviewLink
          href={resolvedUrl}
          target="_blank"
          rel="noreferrer"
          aria-label="View document"
        >
          <FileText size={26} aria-hidden="true" />
          <span style={{ fontSize: "10px", lineHeight: 1 }}>PDF</span>
        </DocumentPreviewLink>
      )}
      <RemoveDocumentButton
        type="button"
        onClick={onRemove}
        aria-label="Remove document"
      >
        <X aria-hidden="true" />
      </RemoveDocumentButton>
    </DocumentPreview>
  );
};

const ShippingNoteDetails = () => {
  const navigate = useNavigate();
  const { scheduleGroupId } = useParams();
  const parsedScheduleGroupId = Number(scheduleGroupId);
  const previewUrlsRef = useRef(new Set());
  const fileSequence = useRef(0);
  const hasPrefilledRef = useRef(false);
  const [deliveryType, setDeliveryType] = useState(ASN_DELIVERY_TYPES.direct);
  const [ewayBillFiles, setEwayBillFiles] = useState([]);
  const [courierReceiptFiles, setCourierReceiptFiles] = useState([]);
  const [ewayBillNumber, setEwayBillNumber] = useState("");
  const [docketNumber, setDocketNumber] = useState("");
  const [transporterName, setTransporterName] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [driverName, setDriverName] = useState("");
  const [existingEwayBill, setExistingEwayBill] = useState(null);
  const [existingCourierReceipt, setExistingCourierReceipt] = useState(null);
  const [fileErrors, setFileErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [isUpdateComplete, setIsUpdateComplete] = useState(false);

  const {
    getPOScheduleGroupDetails,
    getPOScheduleGroupDetailsStatus,
    poScheduleGroupDetails,
    poScheduleGroupDetailsError,
    shippingNoteSelection,
    clearShippingNoteSelection,
    uploadPlanShipmentASNDocument,
    createAdvanceShippingNote,
    createAdvanceShippingNoteResponse,
    createAdvanceShippingNoteError,
    resetCreateAdvanceShippingNote,
    getAdvanceShippingNoteDetails,
    advanceShippingNoteDetails,
    updateAdvanceShippingNote,
    updateAdvanceShippingNoteError,
    resetUpdateAdvanceShippingNote,
    planShipmentHelpSections,
  } = usePlanShipmentStore();

  const vendorId = window.localStorage.getItem("outlet_id");
  const hasValidVendorId =
    Number.isInteger(Number(vendorId)) && Number(vendorId) > 0;
  const hasValidScheduleGroupId =
    Number.isInteger(parsedScheduleGroupId) && parsedScheduleGroupId > 0;
  const hasMatchingSelection =
    Number(shippingNoteSelection?.schedule_group_id) ===
      parsedScheduleGroupId &&
    Array.isArray(shippingNoteSelection?.vendor_invoice_ids) &&
    shippingNoteSelection.vendor_invoice_ids.length > 0;
  const isEditingShippingNote =
    hasMatchingSelection && Number(shippingNoteSelection?.asn_id) > 0;
  const editingAsnId = isEditingShippingNote
    ? Number(shippingNoteSelection.asn_id)
    : 0;
  const asnError = isEditingShippingNote
    ? updateAdvanceShippingNoteError
    : createAdvanceShippingNoteError;

  const fetchScheduleGroupDetails = useCallback(() => {
    if (!hasValidVendorId || !hasValidScheduleGroupId) return;

    getPOScheduleGroupDetails({
      vendor_id: vendorId,
      schedule_group_id: parsedScheduleGroupId,
      page_size: PAGE_SIZE,
      page_number: 1,
    });
  }, [
    getPOScheduleGroupDetails,
    hasValidScheduleGroupId,
    hasValidVendorId,
    parsedScheduleGroupId,
    vendorId,
  ]);

  useEffect(() => {
    resetCreateAdvanceShippingNote();
    resetUpdateAdvanceShippingNote();
    fetchScheduleGroupDetails();
  }, [
    fetchScheduleGroupDetails,
    resetCreateAdvanceShippingNote,
    resetUpdateAdvanceShippingNote,
  ]);

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((previewUrl) => {
        window.URL.revokeObjectURL(previewUrl);
      });
    },
    []
  );

  useEffect(() => {
    if (!isEditingShippingNote || !advanceShippingNoteDetails || hasPrefilledRef.current) return;
    hasPrefilledRef.current = true;

    const delivery = advanceShippingNoteDetails.delivery_details || {};
    const rawType = String(delivery.delivery_type || "").toLowerCase();
    const type = rawType === "courier" ? ASN_DELIVERY_TYPES.courier : ASN_DELIVERY_TYPES.direct;
    setDeliveryType(type);
    setVehicleNumber(String(delivery.vehicle_number || "").toUpperCase());
    setEwayBillNumber(String(delivery.eway_bill_number || ""));

    if (type === ASN_DELIVERY_TYPES.direct) {
      setDriverName(String(delivery.da_name || ""));
      setDriverPhone(String(delivery.da_identifier_value || ""));
    } else {
      setTransporterName(String(delivery.da_name || ""));
      setDocketNumber(String(delivery.docket_number || ""));
    }

    const ewayDoc = getShippingNoteDocument(advanceShippingNoteDetails, "eway");
    if (ewayDoc?.document_path) {
      setExistingEwayBill({ path: ewayDoc.document_path, number: ewayDoc.document_number || "" });
      if (!delivery.eway_bill_number && ewayDoc.document_number) {
        setEwayBillNumber(ewayDoc.document_number);
      }
    }

    const courierDoc = getShippingNoteDocument(advanceShippingNoteDetails, "courier");
    if (courierDoc?.document_path) {
      setExistingCourierReceipt({ path: courierDoc.document_path, number: courierDoc.document_number || "" });
      if (!delivery.docket_number && courierDoc.document_number) {
        setDocketNumber(courierDoc.document_number);
      }
    }
  }, [isEditingShippingNote, advanceShippingNoteDetails]);

  const selectedInvoices = useMemo(
    () =>
      getSelectedShippingNoteInvoices(
        poScheduleGroupDetails,
        hasMatchingSelection ? shippingNoteSelection.vendor_invoice_ids : [],
        editingAsnId
      ),
    [
      hasMatchingSelection,
      poScheduleGroupDetails,
      shippingNoteSelection,
      editingAsnId,
    ]
  );
  const allInvoicesById = useMemo(() => {
    const map = {};
    (poScheduleGroupDetails?.purchase_orders || []).forEach((po) => {
      (po.invoices || []).forEach((inv) => {
        const id = Number(inv.vendor_invoice_id);
        if (id > 0) map[id] = inv;
      });
    });
    return map;
  }, [poScheduleGroupDetails]);

  const removedInvoices = useMemo(() => {
    if (!isEditingShippingNote) return [];

    const selectedInvoiceIdSet = new Set(
      selectedInvoices.map((invoice) => Number(invoice.vendor_invoice_id))
    );
    return (shippingNoteSelection?.original_invoice_ids || [])
      .map(Number)
      .filter((invoiceId) => !selectedInvoiceIdSet.has(invoiceId))
      .map((invoiceId) => ({
        vendor_invoice_id: invoiceId,
        invoice_number: allInvoicesById[invoiceId]?.invoice_number || "",
      }));
  }, [isEditingShippingNote, selectedInvoices, shippingNoteSelection, allInvoicesById]);
  const shipmentSummary = useMemo(
    () => getShippingNoteSummary(selectedInvoices),
    [selectedInvoices]
  );
  const purchaseOrderCount = Number.isFinite(
    Number(poScheduleGroupDetails?.purchase_order_count)
  )
    ? Number(poScheduleGroupDetails.purchase_order_count)
    : poScheduleGroupDetails?.purchase_orders?.length || 0;
  const warehouse = poScheduleGroupDetails?.warehouse || {};

  const effectiveEwayBillFiles =
    ewayBillFiles.length === 0 && existingEwayBill ? [{}] : ewayBillFiles;
  const effectiveCourierReceiptFiles =
    courierReceiptFiles.length === 0 && existingCourierReceipt ? [{}] : courierReceiptFiles;
  const validationErrors = validateShippingNoteForm({
    deliveryType,
    ewayBillFiles: effectiveEwayBillFiles,
    ewayBillNumber,
    courierReceiptFiles: effectiveCourierReceiptFiles,
    docketNumber,
    transporterName,
    vehicleNumber,
    driverPhone,
    driverName,
  });
  const canCreateShippingNote =
    selectedInvoices.length > 0 &&
    Object.keys(validationErrors).length === 0 &&
    !fileErrors[DOCUMENT_KEY_EWAY_BILL] &&
    !(
      deliveryType === ASN_DELIVERY_TYPES.courier &&
      fileErrors[DOCUMENT_KEY_COURIER_RECEIPT]
    );
  const isLoading =
    getPOScheduleGroupDetailsStatus === STATUS_BEGIN ||
    !getPOScheduleGroupDetailsStatus;

  const visibleError = (fieldName) =>
    touchedFields[fieldName] ? validationErrors[fieldName] : "";

  const markTouched = (fieldName) => {
    setTouchedFields((currentFields) => ({
      ...currentFields,
      [fieldName]: true,
    }));
  };

  const handlePickContact = async () => {
    if (!("contacts" in navigator && "ContactsManager" in window)) return;
    try {
      const [contact] = await navigator.contacts.select(["tel"], { multiple: false });
      if (!contact) return;
      const rawPhone = (contact.tel?.[0] || "").replace(/\D/g, "").slice(-10);
      if (rawPhone) {
        setDriverPhone(rawPhone);
        markTouched("driverPhone");
      }
    } catch (_) {
      // User cancelled or API not available — silently ignore
    }
  };

  const selectDocumentFiles = (documentKey, event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (selectedFiles.length === 0) return;

    const isPdfUpload =
      selectedFiles.length === 1 &&
      (selectedFiles[0].type === "application/pdf" ||
        /\.pdf$/i.test(selectedFiles[0].name));

    if (!isPdfUpload && selectedFiles.some((file) => !isSupportedInvoiceImage(file))) {
      setFileErrors((currentErrors) => ({
        ...currentErrors,
        [documentKey]: "Only JPEG, PNG images or a single PDF are supported.",
      }));
      return;
    }

    const selectedSize = selectedFiles.reduce(
      (total, file) => total + file.size,
      0
    );

    if (isPdfUpload) {
      // PDF replaces everything — revoke any existing preview URLs first
      const currentFiles =
        documentKey === DOCUMENT_KEY_EWAY_BILL ? ewayBillFiles : courierReceiptFiles;
      currentFiles.forEach((fileItem) => {
        if (fileItem?.preview_url) {
          window.URL.revokeObjectURL(fileItem.preview_url);
          previewUrlsRef.current.delete(fileItem.preview_url);
        }
      });

      if (selectedSize > MAX_ASN_DOCUMENT_SIZE_BYTES) {
        setFileErrors((currentErrors) => ({
          ...currentErrors,
          [documentKey]: "The PDF must be 5 MB or smaller.",
        }));
        return;
      }

      const previewUrl = window.URL.createObjectURL(selectedFiles[0]);
      previewUrlsRef.current.add(previewUrl);
      const fileItem = {
        client_id: `asn-document-${Date.now()}-${fileSequence.current++}`,
        file: selectedFiles[0],
        preview_url: previewUrl,
      };

      if (documentKey === DOCUMENT_KEY_EWAY_BILL) {
        setEwayBillFiles([fileItem]);
      } else {
        setCourierReceiptFiles([fileItem]);
      }
    } else {
      const currentFiles =
        documentKey === DOCUMENT_KEY_EWAY_BILL ? ewayBillFiles : courierReceiptFiles;
      const currentSize = currentFiles.reduce(
        (total, fileItem) => total + fileItem.file.size,
        0
      );
      if (currentSize + selectedSize > MAX_ASN_DOCUMENT_SIZE_BYTES) {
        setFileErrors((currentErrors) => ({
          ...currentErrors,
          [documentKey]: "The combined images must be 5 MB or smaller.",
        }));
        return;
      }

      const fileItems = selectedFiles.map((file) => {
        const previewUrl = window.URL.createObjectURL(file);
        previewUrlsRef.current.add(previewUrl);
        return {
          client_id: `asn-document-${Date.now()}-${fileSequence.current++}`,
          file,
          preview_url: previewUrl,
        };
      });

      if (documentKey === DOCUMENT_KEY_EWAY_BILL) {
        setEwayBillFiles((currentFiles) => [...currentFiles, ...fileItems]);
      } else {
        setCourierReceiptFiles((currentFiles) => [...currentFiles, ...fileItems]);
      }
    }

    setFileErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[documentKey];
      return nextErrors;
    });
    markTouched(documentKey);
  };

  const removeDocumentFile = (documentKey, fileIndex) => {
    const updateFiles = (currentFiles) => {
      const removedFile = currentFiles[fileIndex];
      if (removedFile?.preview_url) {
        window.URL.revokeObjectURL(removedFile.preview_url);
        previewUrlsRef.current.delete(removedFile.preview_url);
      }
      return currentFiles.filter((_, index) => index !== fileIndex);
    };

    if (documentKey === DOCUMENT_KEY_EWAY_BILL) {
      setEwayBillFiles(updateFiles);
    } else {
      setCourierReceiptFiles(updateFiles);
    }
    markTouched(documentKey);
  };

  const convertAndUploadDocument = async (files, name) => {
    if (files.length === 0) return "";

    // If the user uploaded a single PDF directly, skip image-to-PDF conversion.
    const singleFile = files[0]?.file;
    if (
      files.length === 1 &&
      singleFile &&
      (singleFile.type === "application/pdf" || /\.pdf$/i.test(singleFile.name))
    ) {
      if (singleFile.size > MAX_ASN_DOCUMENT_SIZE_BYTES) {
        throw new Error("The PDF must be 5 MB or smaller.");
      }
      return uploadPlanShipmentASNDocument(singleFile);
    }

    const pdfFile = await convertInvoiceImagesToPdf(
      files.map((fileItem) => fileItem.file),
      name
    );
    if (pdfFile.size > MAX_ASN_DOCUMENT_SIZE_BYTES) {
      throw new Error("The generated PDF must be 5 MB or smaller.");
    }
    return uploadPlanShipmentASNDocument(pdfFile);
  };

  const handleConfirm = async () => {
    if (!canCreateShippingNote || isSubmitting) return;

    setIsSubmitting(true);
    setSubmissionError("");
    try {
      const stripQuery = (path) => String(path || "").split("?")[0];
      const [ewayBillPath, courierReceiptPath] = await Promise.all([
        ewayBillFiles.length > 0
          ? convertAndUploadDocument(ewayBillFiles, "e-way-bill")
          : Promise.resolve(stripQuery(existingEwayBill?.path)),
        deliveryType === ASN_DELIVERY_TYPES.courier
          ? courierReceiptFiles.length > 0
            ? convertAndUploadDocument(courierReceiptFiles, "courier-receipt")
            : Promise.resolve(stripQuery(existingCourierReceipt?.path))
          : Promise.resolve(""),
      ]);

      const asnInfoFields = {
        deliveryType,
        ewayBillPath,
        ewayBillNumber,
        courierReceiptPath,
        docketNumber,
        transporterName,
        vehicleNumber,
        driverPhone,
        driverName,
      };

      if (isEditingShippingNote) {
        const requestBody = buildUpdateAdvanceShippingNoteRequest({
          asnId: editingAsnId,
          selectedInvoices,
          removedInvoices,
          ...asnInfoFields,
        });
        await updateAdvanceShippingNote(requestBody);
        await getAdvanceShippingNoteDetails({
          entity_type: ENTITY_TYPE_SELLER_OUTLET,
          entity_id: vendorId,
          asn_id: editingAsnId,
        });
        setIsUpdateComplete(true);
      } else {
        const requestBody = buildCreateAdvanceShippingNoteRequest({
          selectedInvoices,
          ...asnInfoFields,
        });
        await createAdvanceShippingNote(requestBody);
      }
      setShowConfirmation(false);
    } catch (error) {
      setSubmissionError(
        error?.response?.data?.error?.message ||
          error?.message ||
          "Unable to save the shipping note. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    clearShippingNoteSelection();
    resetCreateAdvanceShippingNote();
    resetUpdateAdvanceShippingNote();
    navigate("/plan-shipment", {
      state: { activeTab: "shipping_notes" },
      replace: true,
    });
  };

  const completedAsnInfo = isEditingShippingNote
    ? advanceShippingNoteDetails
    : createAdvanceShippingNoteResponse;
  const createdShippingNoteNumber = formatShippingNoteNumber(
    completedAsnInfo || {}
  );
  const createdShippingNoteDocument =
    getCreatedShippingNoteDocument(completedAsnInfo);
  const createdShippingNoteUrl = createdShippingNoteDocument?.document_path
    ? resolveDocumentUrl(createdShippingNoteDocument.document_path)
    : "";

  const handleDownloadShippingNote = () => {
    if (!createdShippingNoteUrl) return;

    if (
      window.localStorage.getItem("referrer_platform") === "app" &&
      window.openDocumentHandler
    ) {
      window.openDocumentHandler.postMessage(
        JSON.stringify({
          url: createdShippingNoteUrl,
          title: createdShippingNoteNumber,
          showDownload: true,
        })
      );
      return;
    }

    DownloadInvoice(createdShippingNoteUrl, createdShippingNoteNumber);
  };

  const handleShareShippingNote = async () => {
    if (createdShippingNoteUrl) {
      try {
        const response = await fetch(createdShippingNoteUrl);
        const blob = await response.blob();
        const file = new File([blob], `${createdShippingNoteNumber}.pdf`, {
          type: "application/pdf",
        });

        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            title: createdShippingNoteNumber,
            text: `Shipping note ${createdShippingNoteNumber}`,
            files: [file],
          });
          return;
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    const shareData = {
      title: createdShippingNoteNumber,
      text: `Shipping note ${createdShippingNoteNumber}`,
      ...(createdShippingNoteUrl ? { url: createdShippingNoteUrl } : {}),
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (error?.name !== "AbortError") throw error;
      }
      return;
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(
        createdShippingNoteUrl || createdShippingNoteNumber
      );
    }
  };

  const helpSectionValue =
    deliveryType === ASN_DELIVERY_TYPES.courier ? "COURIER" : "DIRECT_VEHICLE";
  const asnHelpImage =
    planShipmentHelpSections.find(
      (section) => section?.value === helpSectionValue
    )?.cta?.action_data?.image_url || "";

  if (isEditingShippingNote ? isUpdateComplete : createAdvanceShippingNoteResponse?.asn_id) {
    const completedAsnId = isEditingShippingNote
      ? editingAsnId
      : createAdvanceShippingNoteResponse?.asn_id;
    const successTitle = isEditingShippingNote ? "ASN updated" : "ASN created";

    return (
      <DetailPageShell>
        <DetailPageFrame>
          <SuccessPage>
            <DetailHeader>
              <DetailHeaderCopy>
                <h1>{successTitle}</h1>
              </DetailHeaderCopy>
            </DetailHeader>
            <SuccessBody>
              <SuccessIcon>
                <img src="/logos/tick_logo.svg" alt="" aria-hidden="true" />
              </SuccessIcon>
              <SuccessTitle>{successTitle}</SuccessTitle>
              {createdShippingNoteNumber ? (
                <p style={{ margin: "0 0 8px", color: "#59647a", fontSize: "14px", textAlign: "center" }}>
                  {createdShippingNoteNumber}
                </p>
              ) : null}
              <ShareButton
                type="button"
                onClick={handleShareShippingNote}
              >
                Share ASN with driver
                <Share2 size={15} aria-hidden="true" />
              </ShareButton>
              {asnHelpImage && (
                <HelpImageContainer>
                  <img
                    src={asnHelpImage}
                    alt="ASN next steps help guide"
                    style={{
                      width: "100%",
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "8px",
                      display: "block",
                    }}
                  />
                </HelpImageContainer>
              )}
            </SuccessBody>
            <SuccessActions>
              <SuccessActionButton
                type="button"
                onClick={handleDownloadShippingNote}
                disabled={!createdShippingNoteUrl}
              >
                Download
                <Download aria-hidden="true" />
              </SuccessActionButton>
              <SuccessActionButton
                type="button"
                $primary
                onClick={() => navigate(`/plan-shipment/shipping-notes/${completedAsnId}`)}
              >
                View ASN
              </SuccessActionButton>
            </SuccessActions>
          </SuccessPage>
        </DetailPageFrame>
      </DetailPageShell>
    );
  }

  return (
    <DetailPageShell>
      <DetailPageFrame>
        <DetailHeader>
          <HeaderIconButton
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ChevronLeft aria-hidden="true" />
          </HeaderIconButton>
          <DetailHeaderCopy>
            <h1>
              {formatScheduleGroupNumber(
                poScheduleGroupDetails || {
                  schedule_group_id: parsedScheduleGroupId,
                }
              )}
            </h1>
            <span>
              {formatPurchaseOrderCount(purchaseOrderCount)}
              {" • "}
              {formatWeight(poScheduleGroupDetails?.total_weight_in_kg)}
            </span>
          </DetailHeaderCopy>
        </DetailHeader>

        <DetailStatus>
          {formatStatus(poScheduleGroupDetails?.status)}
        </DetailStatus>

        <DetailScrollArea>
          {(!hasValidVendorId || !hasValidScheduleGroupId) && (
            <ShippingNoteContent>
              <EmptyState>
                <StateTitle>Unable to load shipment details</StateTitle>
                <ErrorMessage>
                  The outlet or PO schedule group is invalid.
                </ErrorMessage>
              </EmptyState>
            </ShippingNoteContent>
          )}

          {hasValidVendorId && hasValidScheduleGroupId && isLoading ? (
            <ShippingNoteSkeleton />
          ) : null}

          {getPOScheduleGroupDetailsStatus === STATUS_FAIL ? (
            <ShippingNoteContent>
              <EmptyState>
                <StateTitle>Unable to load shipment details</StateTitle>
                <ErrorMessage>{poScheduleGroupDetailsError}</ErrorMessage>
                <RetryButton type="button" onClick={fetchScheduleGroupDetails}>
                  Try again
                </RetryButton>
              </EmptyState>
            </ShippingNoteContent>
          ) : null}

          {getPOScheduleGroupDetailsStatus === STATUS_DONE &&
          (!hasMatchingSelection || selectedInvoices.length === 0) ? (
            <ShippingNoteContent>
              <EmptyState>
                <StateTitle>No invoices selected</StateTitle>
                <ErrorMessage>
                  Go back and select at least one invoice without a shipping
                  note.
                </ErrorMessage>
                <RetryButton type="button" onClick={() => navigate(-1)}>
                  Select invoices
                </RetryButton>
              </EmptyState>
            </ShippingNoteContent>
          ) : null}

          {getPOScheduleGroupDetailsStatus === STATUS_DONE &&
          poScheduleGroupDetails &&
          selectedInvoices.length > 0 ? (
            <ShippingNoteContent>
              <ShipmentSummaryCard>
                <ShipmentSummaryGrid>
                  <SummaryItem>
                    <SummaryIcon>
                      <CalendarDays aria-hidden="true" />
                    </SummaryIcon>
                    <SummaryLabel>Deliver by</SummaryLabel>
                    <SummaryValue>
                      {formatScheduleDate(poScheduleGroupDetails.delivery_date)}
                    </SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryIcon>
                      <Clock3 aria-hidden="true" />
                    </SummaryIcon>
                    <SummaryLabel>Reporting time</SummaryLabel>
                    <SummaryValue>
                      {formatSlotDisplayName(poScheduleGroupDetails.slot)}
                    </SummaryValue>
                  </SummaryItem>
                  <SummaryItem>
                    <SummaryIcon>
                      <MapPin aria-hidden="true" />
                    </SummaryIcon>
                    <SummaryLabel>Deliver at</SummaryLabel>
                    <SummaryValue>
                      {warehouse.warehouse_name || "—"}
                    </SummaryValue>
                    <MapLinkButton
                      latitude={warehouse.latitude}
                      longitude={warehouse.longitude}
                      fallbackAddress={warehouse.warehouse_name}
                    />
                  </SummaryItem>
                </ShipmentSummaryGrid>
                <SelectedInvoiceSummary>
                  <FileText aria-hidden="true" />
                  {shipmentSummary.invoiceCount} invoices
                  {" • "}
                  {formatInvoiceAmount(shipmentSummary.totalAmount)}
                  {" • "}
                  {formatWeight(shipmentSummary.weightInKg)}
                </SelectedInvoiceSummary>
              </ShipmentSummaryCard>

              <ShipmentDetailsHeading>
                Add shipment details
              </ShipmentDetailsHeading>

              <ShipmentFormCard>
                <DeliveryTypeGrid>
                  <DeliveryTypeButton
                    type="button"
                    $selected={deliveryType === ASN_DELIVERY_TYPES.direct}
                    onClick={() => setDeliveryType(ASN_DELIVERY_TYPES.direct)}
                  >
                    Direct vehicle
                    <DeliveryRadio
                      $selected={deliveryType === ASN_DELIVERY_TYPES.direct}
                    />
                  </DeliveryTypeButton>
                  <DeliveryTypeButton
                    type="button"
                    $selected={deliveryType === ASN_DELIVERY_TYPES.courier}
                    onClick={() => setDeliveryType(ASN_DELIVERY_TYPES.courier)}
                  >
                    Courier / Part load
                    <DeliveryRadio
                      $selected={deliveryType === ASN_DELIVERY_TYPES.courier}
                    />
                  </DeliveryTypeButton>
                </DeliveryTypeGrid>

                <FieldStack>
                  <DocumentUploader
                    id="asn-eway-bill-images"
                    title="Upload e-way bill (Optional)"
                    help="Upload a PDF or one/multiple images. Images are combined into one PDF before upload."
                    files={ewayBillFiles}
                    error={
                      fileErrors[DOCUMENT_KEY_EWAY_BILL] || visibleError(DOCUMENT_KEY_EWAY_BILL)
                    }
                    onSelect={(event) =>
                      selectDocumentFiles(DOCUMENT_KEY_EWAY_BILL, event)
                    }
                    onRemove={(index) =>
                      removeDocumentFile(DOCUMENT_KEY_EWAY_BILL, index)
                    }
                    existingDoc={existingEwayBill}
                    onRemoveExisting={() => setExistingEwayBill(null)}
                  />
                  <FloatingInput
                    label="E way bill number (optional)"
                    value={ewayBillNumber}
                    onBlur={() => markTouched(DOCUMENT_KEY_EWAY_BILL)}
                    onChange={(event) => setEwayBillNumber(event.target.value)}
                  />

                  {deliveryType === ASN_DELIVERY_TYPES.courier ? (
                    <>
                      <DocumentUploader
                        id="asn-courier-receipt-images"
                        title="Upload docket receipt"
                        help="Upload a PDF or one/multiple receipt images. Images are combined into one PDF before upload."
                        files={courierReceiptFiles}
                        error={
                          fileErrors[DOCUMENT_KEY_COURIER_RECEIPT] ||
                          visibleError(DOCUMENT_KEY_COURIER_RECEIPT)
                        }
                        onSelect={(event) =>
                          selectDocumentFiles(DOCUMENT_KEY_COURIER_RECEIPT, event)
                        }
                        onRemove={(index) =>
                          removeDocumentFile(DOCUMENT_KEY_COURIER_RECEIPT, index)
                        }
                        existingDoc={existingCourierReceipt}
                        onRemoveExisting={() => setExistingCourierReceipt(null)}
                      />
                      <FloatingInput
                        label="Docket number"
                        value={docketNumber}
                        error={visibleError("docketNumber")}
                        onBlur={() => markTouched("docketNumber")}
                        onChange={(event) =>
                          setDocketNumber(event.target.value)
                        }
                      />
                      <TransporterSelect
                        value={transporterName}
                        onChange={setTransporterName}
                        onBlur={() => markTouched("transporterName")}
                        options={ASN_TRANSPORTERS}
                        label="Transporter name"
                        error={visibleError("transporterName")}
                        touched={touchedFields.transporterName}
                      />
                      <FloatingInput
                        label="Vehicle number (optional)"
                        value={vehicleNumber}
                        error={visibleError("vehicleNumber")}
                        onBlur={() => markTouched("vehicleNumber")}
                        onChange={(event) =>
                          setVehicleNumber(event.target.value.toUpperCase())
                        }
                        maxLength={15}
                      />
                    </>
                  ) : (
                    <>
                      <FloatingInput
                        label="Vehicle number"
                        value={vehicleNumber}
                        error={visibleError("vehicleNumber")}
                        onBlur={() => markTouched("vehicleNumber")}
                        onChange={(event) =>
                          setVehicleNumber(event.target.value.toUpperCase())
                        }
                        maxLength={15}
                      />
                      <FieldWrapper>
                        <FloatingInput
                          label="Driver phone"
                          value={driverPhone}
                          error={visibleError("driverPhone")}
                          onBlur={() => markTouched("driverPhone")}
                          onChange={(event) =>
                            setDriverPhone(
                              event.target.value.replace(/\D/g, "").slice(0, 10)
                            )
                          }
                          inputMode="numeric"
                          suffix={
                            <InputSuffixButton
                              type="button"
                              aria-label="Pick contact from phonebook"
                              onClick={handlePickContact}
                            >
                              <BookUser aria-hidden="true" />
                            </InputSuffixButton>
                          }
                        />
                        <PhoneDisclaimer>
                          Entry token will be sent to this driver’s phone number
                          for inwarding at the warehouse.
                        </PhoneDisclaimer>
                      </FieldWrapper>
                      <FloatingInput
                        label="Driver name"
                        value={driverName}
                        error={visibleError("driverName")}
                        onBlur={() => markTouched("driverName")}
                        onChange={(event) => setDriverName(event.target.value)}
                        maxLength={50}
                      />
                    </>
                  )}
                </FieldStack>
              </ShipmentFormCard>
            </ShippingNoteContent>
          ) : null}
        </DetailScrollArea>

        {getPOScheduleGroupDetailsStatus === STATUS_DONE &&
        selectedInvoices.length > 0 ? (
          <>
            {(submissionError || asnError) && !showConfirmation ? (
              <SubmissionError>{submissionError || asnError}</SubmissionError>
            ) : null}
            <BottomActions>
              <CreateShippingNoteButton
                type="button"
                disabled={!canCreateShippingNote}
                onClick={() => setShowConfirmation(true)}
              >
                Create ASN
              </CreateShippingNoteButton>
            </BottomActions>
          </>
        ) : null}
      </DetailPageFrame>

      {showConfirmation ? (
        <ModalBackdrop>
          <ConfirmationModal
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-shipment-title"
          >
            <ConfirmationBody>
              <ConfirmationIcon>
                <img src="/logos/info-line.svg" alt="" aria-hidden="true" />
              </ConfirmationIcon>
              <ConfirmationTitle id="confirm-shipment-title">
                Confirm shipment details
              </ConfirmationTitle>
              <ConfirmationSummary>
                <SummaryRow
                  label="Delivery location"
                  value={warehouse.warehouse_name || "—"}
                />
                <SummaryRow
                  label="Delivery date"
                  value={formatScheduleDate(
                    poScheduleGroupDetails?.delivery_date
                  )}
                />
                <SummaryRow
                  label="Reporting time"
                  value={formatSlotDisplayName(poScheduleGroupDetails?.slot)}
                />
                <SummaryRow
                  label="Invoices"
                  value={shipmentSummary.invoiceCount}
                />
                <SummaryRow label="POs" value={shipmentSummary.poCount} />
                <SummaryRow
                  label="Net weight"
                  value={formatWeight(shipmentSummary.weightInKg)}
                />
                <SummaryRow
                  label="Total amount"
                  value={formatInvoiceAmount(shipmentSummary.totalAmount)}
                />
                <SummaryRow
                  label="Delivery type"
                  value={
                    deliveryType === ASN_DELIVERY_TYPES.direct
                      ? "Direct Vehicle"
                      : "Courier / Part load"
                  }
                />
                <SummaryRow
                  label="E way bill no."
                  value={ewayBillNumber.trim() || "—"}
                />
                {deliveryType === ASN_DELIVERY_TYPES.direct ? (
                  <>
                    <SummaryRow label="Vehicle number" value={vehicleNumber} />
                    <SummaryRow label="Driver name" value={driverName} />
                    <SummaryRow label="Driver phone" value={driverPhone} />
                  </>
                ) : (
                  <>
                    <SummaryRow label="Docket number" value={docketNumber} />
                    <SummaryRow
                      label="Transporter name"
                      value={transporterName}
                    />
                    <SummaryRow
                      label="Vehicle number"
                      value={vehicleNumber || "—"}
                    />
                  </>
                )}
              </ConfirmationSummary>
              {submissionError || asnError ? (
                <FieldError>{submissionError || asnError}</FieldError>
              ) : null}
            </ConfirmationBody>
            <ConfirmationActions>
              <ModalButton
                type="button"
                onClick={() => setShowConfirmation(false)}
                disabled={isSubmitting}
              >
                Cancel
              </ModalButton>
              <ModalButton
                type="button"
                $primary
                onClick={handleConfirm}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Spinner aria-label="Creating" />
                ) : (
                  "Yes, confirm"
                )}
              </ModalButton>
            </ConfirmationActions>
          </ConfirmationModal>
        </ModalBackdrop>
      ) : null}
    </DetailPageShell>
  );
};

export default ShippingNoteDetails;
